import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch} from 'react-redux';
import * as Dates from '../utils/Dates';
import * as Time from '../utils/Time';

const useHistoryNavigation = (
  timezone: string,
  scale: Time.Scale,
  setScale: ActionCreatorWithPayload<Time.Scale, string>,
) => {
  const [timestamp, setTimestamp] = React.useState(Dates.now());
  const dispatch = useDispatch();

  const onPrevious = React.useCallback(() => {
    setTimestamp(Dates.getPrevious(timestamp, timezone, scale));
  }, [timestamp, timezone, scale]);

  const onNext = React.useCallback(() => {
    if (
      Dates.differenceWithNow(
        Dates.getScaleStart(
          Dates.getNext(timestamp, timezone, scale),
          timezone,
          scale,
        ),
      ) < 0
    ) {
      setTimestamp(Dates.getNext(timestamp, timezone, scale));
    }
  }, [timestamp, setTimestamp, scale, timezone]);

  const onZoomIn = React.useCallback(() => {
    if (scale === 'year') {
      dispatch(setScale('month'));
    } else if (scale === 'month') {
      dispatch(setScale('week'));
    } else if (scale === 'week') {
      dispatch(setScale('day'));
    }
  }, [dispatch, scale, setScale]);

  const onZoomOut = React.useCallback(() => {
    if (scale === 'day') {
      dispatch(setScale('week'));
    } else if (scale === 'week') {
      dispatch(setScale('month'));
    } else if (scale === 'month') {
      dispatch(setScale('year'));
    }
  }, [dispatch, scale, setScale]);

  return React.useMemo(
    () => ({
      timestamp,
      setTimestamp,
      onPrevious: ![
        Dates.LAST_24_HOURS,
        Dates.LAST_7_DAYS,
        Dates.LAST_30_DAYS,
      ].includes(scale)
        ? onPrevious
        : undefined,
      onNext:
        ![Dates.LAST_24_HOURS, Dates.LAST_7_DAYS, Dates.LAST_30_DAYS].includes(
          scale,
        ) &&
        Dates.differenceWithNow(
          Dates.getScaleStart(
            Dates.getNext(timestamp, timezone, scale),
            timezone,
            scale,
          ),
        ) < 0
          ? onNext
          : undefined,
      onZoomIn,
      onZoomOut,
    }),
    [onNext, onPrevious, onZoomIn, onZoomOut, scale, timestamp, timezone],
  );
};

export default useHistoryNavigation;
